import {
	queryOptions,
	useSuspenseQueries,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { create, keyResolver } from "@yornaath/batshit";
import type { ContentFileDto } from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

const batcher = create({
	fetcher: async (IDs: number[]) => {
		return (await Api.GetMediaBatch(IDs)).data;
	},
	resolver: keyResolver("id"),
});

export const useSuspenseMediaList = (query?: string) => {
	const { id: companyId } = useCompany();

	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Files.List(companyId, query),
		queryFn: async () => {
			return (await Api.GetMediaList(query)).data;
		},
	});

	return data;
};

function mediaItemQueryOptions(
	ID: number | null | undefined,
	companyID: number,
) {
	return queryOptions({
		queryKey: QueryKeys.Files.Single(companyID, ID),
		queryFn: async () => {
			if (ID === null || ID === undefined) {
				return null;
			}
			return batcher.fetch(ID);
		},
	});
}

export function useSuspenseMediaItem<T extends number | null | undefined>(
	ID: T,
) {
	const { id: companyID } = useCompany();

	const { data } = useSuspenseQuery(mediaItemQueryOptions(ID, companyID));

	return data as T extends number ? ContentFileDto : null;
}

export const useSuspenseMedia = (IDs: number[]) => {
	const { id: companyID } = useCompany();
	const queries = useSuspenseQueries({
		queries: IDs.map((ID) => mediaItemQueryOptions(ID, companyID)),
	});

	const data = queries.reduce<ContentFileDto[]>((acc, val) => {
		if (val.data) {
			acc.push(val.data);
		}
		return acc;
	}, []);

	return data;
};
